import axios from 'axios';

/** In-memory access token — NOT persisted to localStorage. */
let _accessToken: string | null = null;

export function getAccessToken(): string | null {
  return _accessToken;
}

export function setAccessToken(token: string | null): void {
  _accessToken = token;
}

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30_000,
  withCredentials: true, // send httpOnly refresh cookie on every request
});

/* ── Request interceptor: attach in-memory JWT token ─────────────────────── */
apiClient.interceptors.request.use((config) => {
  const token = _accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* ── Refresh helper ──────────────────────────────────────────────────────── */
let _refreshPromise: Promise<string | null> | null = null;

async function refreshToken(): Promise<string | null> {
  try {
    const resp = await axios.post(
      `${import.meta.env.VITE_API_URL || ''}/api/auth/refresh`,
      null,
      { withCredentials: true },
    );
    const newToken = resp.data?.access_token ?? null;
    _accessToken = newToken;
    return newToken;
  } catch {
    _accessToken = null;
    return null;
  }
}

/* ── Response interceptor: retry on 401 via refresh ──────────────────────── */
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and NOT a refresh/auth request and not already retried
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/login')
    ) {
      originalRequest._retry = true;

      // Deduplicate concurrent refresh calls
      if (!_refreshPromise) {
        _refreshPromise = refreshToken().finally(() => {
          _refreshPromise = null;
        });
      }

      const newToken = await _refreshPromise;
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      }

      // Refresh failed — clear state and redirect to login
      _accessToken = null;
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    // 503 maintenance mode — dispatch a custom event for the MaintenanceGate overlay
    if (error.response?.status === 503 && error.response.data?.detail === 'maintenance') {
      window.dispatchEvent(new CustomEvent('cm:maintenance'));
    }

    const message =
      error.response?.data?.detail ??
      error.message ??
      'An unexpected error occurred';
    console.error('[API Error]', message);

    return Promise.reject(error);
  },
);

export default apiClient;
